<xemex-card [title]="'installations.general.devices' | translate" [fill]="true">
  <div cardHeaderActions class="flex items-center gap-3">
    <span class="text-sm text-text-secondary">
      {{ 'installations.general.devicesTotal' | translate: { count: devices().length } }}
    </span>
    @if (mayLinkDevice()) {
      <!-- The button is taller than the card title. The negative margin keeps it from growing the
           header, so this title lines up with the titles of the other cards in the row. -->
      <xemex-text-button
        class="-my-2"
        icon="add"
        data-testid="link-device"
        [loading]="linking()"
        (click)="linkDevice()"
      >
        {{ 'installations.link.action' | translate }}
      </xemex-text-button>
    }
  </div>

  @if (linkNotice(); as notice) {
    <div
      class="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border-default bg-surface-page p-3 text-sm"
      data-testid="link-notice"
      [attr.role]="notice.refused ? 'alert' : 'status'"
    >
      <p class="m-0 min-w-0 flex-1" [class.text-text-error]="notice.refused">
        {{ notice.key | translate: notice.params }}
      </p>
      <div class="flex items-center gap-2">
        @if (notice.offersChecklist) {
          <xemex-text-button data-testid="link-notice-checklist" (click)="chooseSources()">
            {{ 'installations.link.chooseSources' | translate }}
          </xemex-text-button>
        }
        <xemex-text-button data-testid="link-notice-dismiss" (click)="dismissLinkNotice()">
          {{ 'installations.link.dismiss' | translate }}
        </xemex-text-button>
      </div>
    </div>
  }

  @if (devices().length) {
    <ul class="m-0 flex list-none flex-col divide-y divide-border-default p-0">
      @for (row of rows(); track row.deviceId) {
        <li class="flex items-start gap-3 py-4 first:pt-0 last:pb-0" data-testid="meter-row">
          <span class="device-card__icon">
            <mat-icon fontSet="material-icons-outlined">router</mat-icon>
          </span>

          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  @if (detailsLoading()) {
                    <xemex-skeleton-block width="96px" height="16px" />
                  } @else {
                    <a
                      class="device-card__name text-text-default hover:underline"
                      [routerLink]="['/', APP_PATHS.Devices, row.deviceId, 'general']"
                      >{{ row.typeName ?? row.ldn }}</a
                    >
                  }
                </div>
                @if (detailsLoading() || row.typeName) {
                  <div class="text-sm text-text-secondary">{{ row.ldn }}</div>
                }
              </div>

              <div class="flex shrink-0 items-center gap-1">
                @if (detailsLoading()) {
                  <xemex-skeleton-block width="56px" height="20px" borderRadius="10px" />
                } @else if (row.status; as status) {
                  <xemex-status-chip dot [status]="status.chip" [label]="status.label" />
                }
                @if (row.canChangeMeterRole || row.readout) {
                  <!-- The negative margin keeps the button from growing the row past the name and
                       LDN beside it. -->
                  <button
                    mat-icon-button
                    type="button"
                    class="device-card__menu -my-2 -mr-2"
                    data-testid="device-actions"
                    [attr.aria-label]="'installations.general.deviceActions' | translate"
                    [disabled]="readingOut() === row.deviceId"
                    [matMenuTriggerFor]="deviceMenu"
                  >
                    @if (readingOut() === row.deviceId) {
                      <xemex-spinner size="sm" />
                    } @else {
                      <mat-icon>more_vert</mat-icon>
                    }
                  </button>
                  <mat-menu #deviceMenu="matMenu" xPosition="before">
                    @if (row.canChangeMeterRole) {
                      <button
                        mat-menu-item
                        type="button"
                        data-testid="change-meter-role"
                        (click)="changeMeterRole(row.deviceId)"
                      >
                        <mat-icon>edit</mat-icon>
                        <span>{{ 'installations.attributes.meterRole.change' | translate }}</span>
                      </button>
                    }
                    @if (row.readout) {
                      <button
                        mat-menu-item
                        type="button"
                        data-testid="device-readout"
                        (click)="readOut(row.deviceId)"
                      >
                        <mat-icon>sync</mat-icon>
                        <span>{{
                          'devices.detailTabs.insights.actions.onDemandReadout' | translate
                        }}</span>
                      </button>
                    }
                  </mat-menu>
                }
              </div>
            </div>

            @if (row.phaseCount || row.meterRole) {
              <div class="device-card__fields">
                @if (row.phaseCount; as phaseCount) {
                  <xemex-detail-field
                    data-testid="phase-count"
                    [label]="'installations.attributes.phaseCount.label' | translate"
                    [loading]="phaseCount.loading"
                  >
                    {{ phaseCount.text }}
                    @if (phaseCount.note) {
                      <span class="text-text-secondary"> · {{ phaseCount.note }}</span>
                    }
                  </xemex-detail-field>
                }
                @if (row.meterRole; as meterRole) {
                  <xemex-detail-field
                    data-testid="meter-role"
                    [label]="'installations.attributes.meterRole.label' | translate"
                    [loading]="meterRole.loading"
                  >
                    {{ meterRole.text }}
                  </xemex-detail-field>
                }
              </div>
            }

            @if (row.feedsNoPoint) {
              <p class="m-0 text-sm text-text-secondary" data-testid="feeds-no-point">
                {{ 'installations.general.feedsNoPoint' | translate }}
              </p>
            }
          </div>
        </li>
      }
    </ul>
  } @else {
    <p class="m-0 text-text-secondary">{{ 'installations.general.noDevices' | translate }}</p>
  }
</xemex-card>
